import { Fabric } from '../models/item/fabric';
import { WorkOrderType } from '../models/order/work-order';

export interface YarnQuantityParams {
  quantityInUOM: number;
  uom: number;
  fabric: Fabric | null;
  orderType: number;
}

/**
 * Utility class for calculating yarn quantities with loss percentages.
 *
 * For GreigeOnly and BGrade work orders, only knitting loss is applied.
 * For all other work orders, both dyeing/finishing loss and knitting loss are applied.
 */
export class YarnQuantityUtil {
  private static readonly UOM_POUNDS = 3;

  /**
   * Determines if the work order type should skip dyeing loss calculation.
   * GreigeOnly and BGrade work orders skip dyeing loss since the fabric won't be dyed.
   */
  static shouldSkipDyeingLoss(orderType: number): boolean {
    return orderType === WorkOrderType.GreigeOnly || orderType === WorkOrderType.BGrade;
  }

  /**
   * Converts quantity to pounds based on UOM.
   * UOM 3 = Pounds (no conversion needed)
   * UOM 5 = Yards (divide by poundToYard ratio)
   */
  static convertToPounds(quantity: number, uom: number, poundToYard: number): number {
    if (uom === YarnQuantityUtil.UOM_POUNDS) {
      return quantity;
    }
    return poundToYard > 0 ? quantity / poundToYard : 0;
  }

  /**
   * Calculates yarn quantity with loss percentages.
   *
   * @param params - The calculation parameters
   * @returns The yarn quantity accounting for losses
   */
  static calculateYarnQuantity(params: YarnQuantityParams): number {
    const { quantityInUOM, uom, fabric, orderType } = params;

    if (!fabric || quantityInUOM <= 0) {
      return 0;
    }

    const poundToYard = fabric.poundToYard ?? 0;
    const quantityInPounds = YarnQuantityUtil.convertToPounds(quantityInUOM, uom, poundToYard);

    if (quantityInPounds <= 0) {
      return 0;
    }

    const knittingLossPercentage = fabric.greige?.knittingProcessLossPercentage ?? 0;
    const dyeingLossPercentage = fabric.finishingDetails?.dyeingAndFinishingLossPercentage ?? 0;

    let yarnQuantity = quantityInPounds;

    // Apply dyeing/finishing loss only if not GreigeOnly or BGrade
    if (!YarnQuantityUtil.shouldSkipDyeingLoss(orderType)) {
      yarnQuantity = (yarnQuantity / (100 - dyeingLossPercentage)) * 100;
    }

    // Always apply knitting loss
    yarnQuantity = (yarnQuantity / (100 - knittingLossPercentage)) * 100;

    return yarnQuantity;
  }

  /**
   * Calculates yarn quantity with loss percentages for a given fabric quantity.
   * This is a convenience method for use in templates or simple calculations.
   *
   * @param quantity - The fabric quantity in pounds
   * @param fabric - The fabric containing loss percentages
   * @param orderType - The work order type to determine if dyeing loss should be applied
   * @returns The yarn quantity accounting for losses
   */
  static calculateWithLoss(quantity: number, fabric: Fabric, orderType: number): number {
    if (!fabric || quantity <= 0) {
      return 0;
    }

    const knittingLossPercentage = fabric.greige?.knittingProcessLossPercentage ?? 0;
    const dyeingLossPercentage = fabric.finishingDetails?.dyeingAndFinishingLossPercentage ?? 0;

    let yarnQuantity = quantity;

    // Apply dyeing/finishing loss only if not GreigeOnly or BGrade
    if (!YarnQuantityUtil.shouldSkipDyeingLoss(orderType)) {
      const dyeingMultiplier = (100 - dyeingLossPercentage) / 100;
      yarnQuantity = dyeingMultiplier > 0 ? yarnQuantity / dyeingMultiplier : yarnQuantity;
    }

    // Always apply knitting loss
    const knittingMultiplier = (100 - knittingLossPercentage) / 100;
    yarnQuantity = knittingMultiplier > 0 ? yarnQuantity / knittingMultiplier : yarnQuantity;

    return yarnQuantity;
  }
}
